<script setup lang="ts">
import { computed } from 'vue';
import {
    formatBibliographyEntry,
    sortForStyle,
} from '@/lib/citations/formatters';
import type { CitationStyle } from '@/lib/citations/formatters';
import type { ReferenceItem } from './references/ReferenceListItem.vue';

const props = defineProps<{
    references: ReferenceItem[];
    citedIds: ReadonlySet<string>;
    /** Order-of-appearance IDs for Vancouver numbering. Falls back to citedIds insertion order. */
    citedOrder?: string[];
    style?: CitationStyle;
}>();

const activeStyle = computed<CitationStyle>(() => props.style ?? 'apa');

/** Exclude entries with no usable bibliographic data. */
function isUsable(ref: ReferenceItem): boolean {
    if (!ref.title?.trim()) {
        return false;
    }

    const authors = Array.isArray(ref.authors)
        ? ref.authors
        : [ref.authors ?? ''];
    const hasAuthor = authors.some((a) => a.trim().length > 0);
    const hasDoi = !!(ref.doi || ref.csl?.DOI);
    const hasUrl = !!ref.csl?.URL;

    return hasAuthor || hasDoi || hasUrl;
}

const cited = computed<ReferenceItem[]>(() => {
    if (!props.citedIds?.size) {
        return [];
    }

    const filtered = props.references.filter(
        (r) => props.citedIds.has(r.id) && isUsable(r),
    );

    if (activeStyle.value === 'vancouver') {
        // Preserve order of appearance
        if (props.citedOrder?.length) {
            const map = new Map(filtered.map((r) => [r.id, r]));

            return props.citedOrder
                .map((id) => map.get(id))
                .filter((r): r is ReferenceItem => r !== undefined);
        }

        return filtered; // fallback: insertion order
    }

    return sortForStyle(filtered, activeStyle.value);
});
</script>

<template>
    <div class="space-y-2">
        <p v-if="!cited.length" class="text-sm text-muted-foreground italic">
            Aucune citation dans le document pour l'instant.
        </p>

        <ol v-else class="list-none space-y-2">
            <li
                v-for="(ref, index) in cited"
                :key="ref.id"
                class="text-sm leading-snug"
            >
                <span>{{
                    formatBibliographyEntry(ref, activeStyle, index + 1)
                }}</span>
            </li>
        </ol>
    </div>
</template>
